// ─────────────────────────────────────────────────────────────────────────────
//  graphs/index.jsx  — ChartFactory
//
//  Pure router. No data transformation, no tooltip, no legend logic here.
//  Each chart component is fully self-contained and handles its own transform.
//
//  USAGE (unchanged from before):
//    <ChartFactory chartData={...} chartOptions={{ transform: true }} group_by={['location','kpi']} />
//
//  chartOptions.transform=true  → each chart runs its own transformData()
//  chartOptions.transform=false → chartData is already in the final shape
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import { ErrorMessage } from "../mainUnit";
import { BarChart }             from "./BarChart";
import { HorizontalBarChart }   from "./HorizontalBarChart";
import { LineChart }            from "./LineChart";
import { AreaChart }            from "./AreaChart";
import { PieChart, DonutChart } from "./PieDonutChart";
import { RadarChart }           from "./RadarChart";

export const ChartFactory = ({ chartData, chartOptions = {}, group_by = [] }) => {
    if (chartData?.errorMessage) return <ErrorMessage message={chartData.errorMessage} />;
    if (!chartData)              return <ErrorMessage message="No data available" />;

    const groupedField = group_by.length > 0
        ? group_by
        : [chartData.xAxisField, chartData.stackByField].filter(Boolean);

    if (groupedField.length > 2) return <ErrorMessage message="Maximum two fields allowed for grouping" />;

    // chartType comes from the raw chartData when transforming,
    // or from transformedChartData.graphType when data is pre-shaped
    const chartType = (chartData.graphType || chartData.chartType || 'bar').toLowerCase();

    const sharedProps = { chartData, chartOptions, groupedField };

    try {
        switch (chartType) {
            case 'bar':    return <HorizontalBarChart {...sharedProps} />;
            case 'column': return <BarChart           {...sharedProps} />;
            case 'line':   return <LineChart          {...sharedProps} />;
            case 'area':   return <AreaChart          {...sharedProps} />;
            case 'pie':    return <PieChart           {...sharedProps} />;
            case 'donut':  return <DonutChart         {...sharedProps} />;
            case 'radar':  return <RadarChart         {...sharedProps} />;
            default:       return <HorizontalBarChart {...sharedProps} />;
        }
    } catch (error) {
        console.error('Chart error:', error);
        return <ErrorMessage message={error.message} />;
    }
};
